import { Link, useLocation, useParams, useNavigate } from "react-router-dom";
import { useMemo, useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import { surveyForms } from "../../utils/forms";
import { buildKioskApiPayload, KIOSK_SECTION_FIELDS, KIOSK_COMMON_FIELDS, parseKioskApiResponse } from "../../utils/payloads/kioskPayload";
import { buildKitchenApiPayload, parseKitchenApiResponse, KITCHEN_SECTION_FIELDS, KITCHEN_COMMON_FIELDS } from "../../utils/payloads/kitchenPayload";
import { buildKitchenDiningApiPayload, parseKitchenDiningApiResponse, KITCHEN_DINING_SECTION_FIELDS, KITCHEN_DINING_COMMON_FIELDS } from "../../utils/payloads/KitchenCumDiningPayload";
import { selectUserLocation } from "../../features/location/locationSelectors";
import { checkLocationPermission } from "../../features/location/locationSlice";
import CameraCapture from "../../components/survey/CameraCapture";
import { submitKioskSurvey, submitKitchenSurvey, submitKitchenDiningSurvey } from "../../features/survey/surveyActions";

import { useTranslation } from "../../hooks/useTranslations";

const todayISO = () => new Date().toISOString().split("T")[0];

const getInitialAnswers = (form) => {
  const answers = {};
  form.sections.forEach((section) => {
    section.questions.forEach((question) => {
      answers[question.id] = question.type === "checkbox" ? [] : "";
      (question.fields || []).forEach((field) => {
        answers[`${question.id}_${field.id}`] = field.type === "file" ? null : "";
      });
    });
  });
  return answers;
};

const isFieldVisible = (question, field, answers) => {
  if (!field.showWhen || field.showWhen.length === 0) return true;
  const answer = answers[question.id];
  if (Array.isArray(answer)) return answer.some((v) => field.showWhen.includes(v));
  return field.showWhen.includes(answer);
};

// ── Section status card ───────────────────────────────────────────────────────
function SectionCard({ section, index, isComplete, onClick, surveyType }) {
  const questionCount = section.questions.length;
  
 const { titleTranslate } = useTranslation();

  return (
    <button
      type="button"
      onClick={onClick}
      className={`
        relative flex flex-col justify-between text-left rounded-2xl border bg-surface p-4
        transition-colors hover:border-primary min-h-[110px]
        ${isComplete ? "border-green-600" : "border-border"}
      `}
    >
      <div className="flex items-start justify-between">
        <span
          className={`
            inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-semibold
            ${isComplete
              ? "bg-green-100 text-green-800"
              : "bg-red-20 text-primary"}
          `}
        >
          {section.id.toUpperCase()}
        </span>
        {isComplete && (
          <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </div>

      <div>
        <p className="text-sm font-semibold leading-snug mt-2">
          {titleTranslate(
            surveyType,
            section.id,
            section.title
          )}
        </p>
        <p className="text-xs text-text-muted mt-0.5">
          {questionCount} question{questionCount !== 1 ? "s" : ""}
        </p>
        <div className="mt-2 h-1 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full bg-green-500 rounded-full transition-all duration-300"
            style={{ width: isComplete ? "100%" : "0%" }}
          />
        </div>
      </div>
    </button>
  );
}

// ── Fullscreen section popup ──────────────────────────────────────────────────
function SectionPopup({
  section,
  allSections,
  currentIndex,
  answers,
  onAnswer,
  onToggleCheckbox,
  onUpdateField,
  onBack,
  onSave,
  completedSections,
  isLastSection,
  isSaving,
  surveyType
}) {

const {t} = useTranslation();
const { tq } = useTranslation(surveyType);
const {tqh} = useTranslation(surveyType);
const {titleTranslate} = useTranslation(surveyType);
const {tOption} = useTranslation();

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center gap-4 px-5 py-4 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <span className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-xl bg-primary/10 text-primary text-sm font-bold">
            {section.id.toUpperCase()}
          </span>
          <div className="min-w-0">
            <h2 className="text-base font-bold truncate">{titleTranslate(surveyType, section.id, section.title)}</h2>
            <p className="text-xs text-text-muted">
              {section.questions.length} question{section.questions.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {/* Section progress dots */}
        <div className="hidden sm:flex items-center gap-1.5">
          {allSections.map((s, i) => (
            <span
              key={s.id}
              className={`
                rounded-full transition-all
                ${i === currentIndex
                  ? "w-4 h-2 bg-primary"
                  : completedSections.has(s.id)
                    ? "w-2 h-2 bg-green-500"
                    : "w-2 h-2 bg-border"}
              `}
            />
          ))}
        </div>

        <span className="text-xs text-text-muted shrink-0">
          {currentIndex + 1} / {allSections.length}
        </span>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4">
          {section.questions.map((question) => (
            <div
              key={question.id}
              className="bg-surface border border-border rounded-2xl p-5 space-y-4"
            >
              <div className="flex gap-3">
                <span className="shrink-0 inline-flex items-center justify-center min-w-[3rem] h-8 px-2 rounded-lg bg-primary/10 text-primary text-sm font-bold">
                  {question.label}
                </span>
                <div className="space-y-1">
                  <label className="text-sm font-semibold leading-relaxed">
                    {tq(surveyType, question.id, question.question)}
                  </label>
                  {question.helperText && (
                    <p className="text-xs text-text-muted">
                      {tqh(
                        surveyType,
                        `${question.id}_HELPER`,
                        question.helperText
                      )}
                    </p>
                  )}
                </div>
              </div>

              {question.type === "radio" && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {question.options.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-center gap-3 rounded-xl border border-border bg-background px-4 py-3 text-sm font-semibold cursor-pointer hover:border-primary"
                    >
                      <input
                        type="radio"
                        name={question.id}
                        value={option.value}
                        checked={answers[question.id] === option.value}
                        onChange={() => onAnswer(question.id, option.value)}
                        className="accent-primary"
                      />
                      {tOption(option.label)}
                    </label>
                  ))}
                </div>
              )}

              {question.type === "checkbox" && (
                <div className="grid grid-cols-1 gap-2">
                  {question.options.map((option) => (
                    <label
                      key={option.value}
                      className="flex items-start gap-3 rounded-xl border border-border bg-background px-4 py-3 text-sm font-semibold cursor-pointer hover:border-primary"
                    >
                      <input
                        type="checkbox"
                        value={option.value}
                        checked={(answers[question.id] || []).includes(option.value)}
                        onChange={() => onToggleCheckbox(question.id, option.value)}
                        className="mt-1 accent-primary"
                      />
                      {tOption(option.label)}
                    </label>
                  ))}
                </div>
              )}

              {question.type === "number" && (
                <input
                  type="number"
                  value={answers[question.id]}
                  onChange={(e) => onAnswer(question.id, e.target.value)}
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter number"
                />
              )}

              {question.type === "text" && (
                <input
                  type="text"
                  value={answers[question.id]}
                  onChange={(e) => onAnswer(question.id, e.target.value)}
                  className="w-full rounded-xl border border-border bg-background px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter response"
                />
              )}

              {question.type === "textarea" && (
                <textarea
                  value={answers[question.id]}
                  onChange={(e) => onAnswer(question.id, e.target.value)}
                  className="w-full min-h-28 rounded-xl border border-border bg-background px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Enter details"
                />
              )}

              {/* Sub-fields */}
              {(question.fields || [])
                .filter((field) => isFieldVisible(question, field, answers))
                .map((field) => (
                  <div
                    key={field.id}
                    className="rounded-xl border border-border bg-background p-4 space-y-2"
                  >
                    <label className="block text-sm font-bold text-text">
                      {tOption(field.label)}
                    </label>

                    {field.type === "textarea" && (
                      <textarea
                        value={answers[`${question.id}_${field.id}`]}
                        onChange={(e) => onUpdateField(question.id, field.id, e.target.value)}
                        className="w-full min-h-24 rounded-xl border border-border bg-surface px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                        placeholder={field.placeholder || "Enter details"}
                      />
                    )}

                    {field.type === "date" && (
                      <input
                        type="date"
                        value={answers[`${question.id}_${field.id}`]}
                        onChange={(e) => onUpdateField(question.id, field.id, e.target.value)}
                        className="w-full rounded-xl border border-border bg-surface px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    )}

                    {field.type === "time" && (
                      <input
                        type="time"
                        value={answers[`${question.id}_${field.id}`]}
                        onChange={(e) => onUpdateField(question.id, field.id, e.target.value)}
                        className="w-full rounded-xl border border-border bg-surface px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    )}

                    {field.type === "file" && field.id === "photo" && (
                      <CameraCapture
                        value={answers[`${question.id}_${field.id}`]}
                        helperText={field.helperText}
                        onChange={(file) => onUpdateField(question.id, field.id, file)}
                      />
                    )}

                    {field.type === "file" && field.id !== "photo" && (
                      <>
                        <input
                          type="file"
                          accept={field.accept}
                          capture={field.capture}
                          onChange={(e) =>
                            onUpdateField(question.id, field.id, e.target.files?.[0] || null)
                          }
                          className="block w-full text-sm text-text-muted file:mr-4 file:rounded-lg file:border-0 file:bg-primary file:px-4 file:py-2.5 file:text-sm file:font-semibold file:text-white"
                        />
                        {field.helperText && (
                          <p className="text-xs text-text-muted">{field.helperText}</p>
                        )}
                      </>
                    )}
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-border bg-surface flex-shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="px-5 py-2.5 rounded-xl border border-border bg-background font-semibold text-sm hover:bg-gray-50"
        >
         {t("survey.backtosection")}
        </button>
        <button
          type="button"
          onClick={onSave}
          disabled={isSaving}
          className="px-6 py-2.5 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-sm shadow-sm disabled:opacity-50"
        >
          {isSaving
            ? t("survey.saving")
            : isLastSection
              ? t("survey.saving")
              : t("survey.save&continue")}
        </button>
      </div>
    </div>
  );
}

const flattenObj = (ob) => {
  let result = {};
  for (const i in ob) {
    if (typeof ob[i] === 'object' && !Array.isArray(ob[i]) && ob[i] !== null) {
      const temp = flattenObj(ob[i]);
      for (const j in temp) {
        result[j] = temp[j];
      }
    } else {
      result[i] = ob[i];
    }
  }
  return result;
};

// ── Main page ─────────────────────────────────────────────────────────────────
function SurveyPage() {
  const { surveyType } = useParams();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const previousRoute = state?.from || "/home";
  // console.log(state); 
  // console.log(previousRoute);

  const routeName =
    previousRoute === "/home"
      ? "Search"
      : previousRoute
        .replace("/", "")
        .split("-")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");

  const userLocation = useSelector(selectUserLocation);
  const { loading } = useSelector((s) => s.survey);
  const form = surveyForms[surveyType];

  const { t } = useTranslation();

  const [answers, setAnswers] = useState(() => {
    let initial = form ? getInitialAnswers(form) : {};
    if (state?.existingSurveyData) {
      const flattened = flattenObj(state.existingSurveyData);
      let parsed = { answers: {} };
      if (surveyType === "kiosk") {
        parsed = parseKioskApiResponse(flattened);
      } else if (surveyType === "kitchen" || surveyType === "kitchen-cum-dining") {
        parsed = parseKitchenApiResponse(flattened);
      } else if (surveyType === "kitchen-cum-dining") {
        parsed = parseKitchenDiningApiResponse(flattened);
      }
      // Merge initial with parsed answers, replacing only truthy parsed values
      Object.keys(parsed.answers).forEach(k => {
        if (parsed.answers[k] !== "" && parsed.answers[k] !== null && parsed.answers[k] !== undefined && (!Array.isArray(parsed.answers[k]) || parsed.answers[k].length > 0)) {
          initial[k] = parsed.answers[k];
        }
      });
    }
    return initial;
  });

  const [address, setAddress] = useState(state?.canteen?.kitchen_address || "");

  const [completedSections, setCompletedSections] = useState(() => {
    let sections = new Set();
    if (state?.existingSurveyData) {
      const flattened = flattenObj(state.existingSurveyData);
      let parsed = { completedSections: [] };
      if (surveyType === "kiosk") {
        parsed = parseKioskApiResponse(flattened);
      } else if (surveyType === "kitchen" || surveyType === "kitchen-cum-dining") {
        parsed = parseKitchenApiResponse(flattened);
      }
      sections = new Set(parsed.completedSections);
    }
    return sections;
  });

  const [activeSection, setActiveSection] = useState(null); // index | null

  useEffect(() => {
    dispatch(checkLocationPermission());
  }, [dispatch]);

  const totalQuestions = useMemo(() => {
    if (!form) return 0;
    return form.sections.reduce((t, s) => t + s.questions.length, 0);
  }, [form]);

  const allComplete = form && completedSections.size === form.sections.length;

  // ── Answer handlers (stable refs so SectionPopup doesn't re-render) ─────────
  const onAnswer = useCallback((questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  const onToggleCheckbox = useCallback((questionId, value) => {
    setAnswers((prev) => {
      const selected = prev[questionId] || [];
      const next = selected.includes(value)
        ? selected.filter((v) => v !== value)
        : [...selected, value];
      return { ...prev, [questionId]: next };
    });
  }, []);

  const onUpdateField = useCallback((questionId, fieldId, value) => {
    setAnswers((prev) => ({ ...prev, [`${questionId}_${fieldId}`]: value }));
  }, []);

  // ── Save a section and auto-advance ─────────────────────────────────────────
  const handleSaveSection = useCallback(() => {
    const section = form.sections[activeSection];
    const sectionId = section.id.toLowerCase();

    // Mark section complete
    setCompletedSections((prev) => new Set([...prev, section.id]));

    if (surveyType === "kiosk") {
      const { payload, logPayload } = buildKioskApiPayload({
        answers,
        canteen: state?.canteen || null,
        locationState: {
          districtId: state?.districtId || "",
          municipalityId: state?.municipalityId || "",
          wardId: state?.wardId || "",
        },
        userLocation,
        reportingDate: todayISO(),
        address,
      });

      const sectionSpecificFields = KIOSK_SECTION_FIELDS[sectionId] || [];
      const fieldsToKeep = [...KIOSK_COMMON_FIELDS, ...sectionSpecificFields];

      const sectionPayload = {};
      const sectionLogPayload = {};

      fieldsToKeep.forEach((field) => {
        if (payload[field] !== undefined) {
          sectionPayload[field] = payload[field];
        }
        if (logPayload[field] !== undefined) {
          sectionLogPayload[field] = logPayload[field];
        }
      });

      // console.group(`Kiosk Survey — Section ${section.id.toUpperCase()} Save Payload`);
      // console.log(sectionLogPayload);
      // console.groupEnd();

      dispatch(submitKioskSurvey(sectionPayload))
        .then(() => {
          const nextIdx = activeSection + 1;

          if (nextIdx < form.sections.length) {
            setActiveSection(nextIdx);
          } else {
            setActiveSection(null);
          }
        })
        .catch((err) => {
          alert(err || "Failed to save section");
        });
    } else if (surveyType === "kitchen-cum-dining") { /*save kitchen-cum-dining section here*/
      const { payload, logPayload } = buildKitchenDiningApiPayload({
        answers,
        canteen: state?.canteen || null,
        locationState: {
          districtId: state?.districtId || "",
          municipalityId: state?.municipalityId || "",
          wardId: state?.wardId || "",
        },
        userLocation,
        reportingDate: todayISO(),
        address,
      });

      const sectionSpecificFields = KITCHEN_DINING_SECTION_FIELDS[sectionId] || [];
      const fieldsToKeep = [...KITCHEN_DINING_COMMON_FIELDS, ...sectionSpecificFields];

      const sectionPayload = {};
      const sectionLogPayload = {};

      fieldsToKeep.forEach((field) => {
        if (payload[field] !== undefined) {
          sectionPayload[field] = payload[field];
        }
        if (logPayload[field] !== undefined) {
          sectionLogPayload[field] = logPayload[field];
        }
      });

      // console.group(`Kitchen-cum-Dining Survey — Section ${section.id.toUpperCase()} Save Payload`);
      // console.log(sectionLogPayload);
      // console.groupEnd();

      dispatch(submitKitchenDiningSurvey(sectionPayload))
        .then(() => {
          const nextIdx = activeSection + 1;

          if (nextIdx < form.sections.length) {
            setActiveSection(nextIdx);
          } else {
            setActiveSection(null);
          }
        })
        .catch((err) => {
          alert(err || "Failed to save section");
        });
    } else if (surveyType === "kitchen") {
      const { payload, logPayload } = buildKitchenApiPayload({
        answers,
        canteen: state?.canteen || null,
        locationState: {
          districtId: state?.districtId || "",
          municipalityId: state?.municipalityId || "",
          wardId: state?.wardId || "",
        },
        userLocation,
        reportingDate: todayISO(),
        address,
      });

      const sectionSpecificFields = KITCHEN_SECTION_FIELDS[sectionId] || [];
      const fieldsToKeep = [...KITCHEN_COMMON_FIELDS, ...sectionSpecificFields];

      const sectionPayload = {};
      const sectionLogPayload = {};

      fieldsToKeep.forEach((field) => {
        if (payload[field] !== undefined) {
          sectionPayload[field] = payload[field];
        }
        if (logPayload[field] !== undefined) {
          sectionLogPayload[field] = logPayload[field];
        }
      });

      // console.group(`Kitchen Survey — Section ${section.id.toUpperCase()} Save Payload`);
      // console.log(sectionLogPayload);
      // console.groupEnd();

      dispatch(submitKitchenSurvey(sectionPayload))
        .then(() => {
          const nextIdx = activeSection + 1;

          if (nextIdx < form.sections.length) {
            setActiveSection(nextIdx);
          } else {
            setActiveSection(null);
          }
        })
        .catch((err) => {
          alert(err || "Failed to save section");
        });
    }
  }, [
    activeSection,
    form,
    surveyType,
    answers,
    state,
    userLocation,
    address,
    dispatch,
  ]);

  // ── Final submit ─────────────────────────────────────────────────────────────
  const handleFinalSubmit = () => {

    const commonPayload = {
      reporting_date: todayISO(),
      kiosk_location_fulladdress: address || state?.canteen?.kitchen_address || "",
      lattitude_longitude: userLocation
        ? `${userLocation.latitude},${userLocation.longitude}`
        : "",
      district_id: state?.districtId || "",
      municipality_id: state?.municipalityId || "",
      ward_id: state?.wardId || "",
      canteens_id: state?.canteen?.canteen_type || "",
      canteen_type_id: state?.canteen?.id || "",

      // FINAL FLAGS
      check_survey_completed: 1,
      is_total_survey_complete: 1,
    };

    // console.group("Final Submit Payload");
    // console.log(commonPayload);
    // console.groupEnd();

    if (surveyType === "kiosk") {

      dispatch(submitKioskSurvey(commonPayload))
        .then(() => {
          alert("Survey submitted successfully!");
          navigate("/home");
        })
        .catch((err) => alert(err || "Failed to submit survey"));

    } else if (surveyType === "kitchen") {

      dispatch(submitKitchenSurvey(commonPayload))
        .then(() => {
          alert("Survey submitted successfully!");
          navigate("/home");
        })
        .catch((err) => alert(err || "Failed to submit survey"));

    } else if (surveyType === "kitchen-cum-dining") {

      dispatch(submitKitchenDiningSurvey(commonPayload))
        .then(() => {
          alert("Survey submitted successfully!");
          navigate("/home");
        })
        .catch((err) => alert(err || "Failed to submit survey"));
    }
  };

  if (!form) {
    return (
      <main className="min-h-screen bg-background text-text px-4 py-10">
        <div className="max-w-4xl mx-auto bg-surface border border-border rounded-2xl p-6 shadow-sm">
          <h2 className="text-2xl font-bold">Survey type not found</h2>
          <p className="text-text-muted mt-2">
            Please go back and start the survey from a canteen card.
          </p>
          <Link
            to="/home"
            className="inline-flex mt-6 px-5 py-2.5 rounded-xl bg-primary text-white font-semibold"
          >
            Back to Home
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background text-text pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* ── Header ───────────────────────────────────────────────────────── */}
        <section className="bg-surface border border-border rounded-2xl p-5 sm:p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-sm font-semibold text-primary hover:text-orange-600"
              >
                ← Back to {routeName}
              </button>
              <h1 className="text-2xl sm:text-3xl font-extrabold mt-2">
                  {t(`surveyForms.${surveyType}.title`)}
              </h1>
              <p className="text-text-muted mt-1">{t(`surveyForms.${surveyType}.description`)}</p>
            </div>
            <span className="self-start bg-primary/10 text-primary text-sm font-bold px-4 py-1.5 rounded-full border border-primary/20">
              {totalQuestions} Questions
            </span>
          </div>

          {/* Info Strip */}
          <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-text-muted border-t border-border pt-4">
            
            {state?.canteen?.area_name && (
              <div>
                <span className="font-semibold text-text">Area:</span>{" "}
                {state.canteen.area_name}
              </div>
            )}
          
            {state?.canteen?.kitchen_address && (
              <div>
                <span className="font-semibold text-text">Address:</span>{" "}
                {state.canteen.kitchen_address}
              </div>
            )}
          
            {/* {userLocation && (
              <>
                <div>
                  <span className="font-semibold text-text">Lat:</span>{" "}
                  {userLocation.latitude.toFixed(6)}
                </div>
            
                <div>
                  <span className="font-semibold text-text">Lng:</span>{" "}
                  {userLocation.longitude.toFixed(6)}
                </div>
            
                <div>
                  <span className="font-semibold text-text">Acc:</span>{" "}
                  {Math.round(userLocation.accuracy)}m
                </div>
              </>
            )} */}
          </div>
        </section>
        

        <section className="bg-surface border border-border rounded-2xl p-5 sm:p-6 shadow-sm">
          {/* Address */}
          <div className="mt-5">
            <label className="block text-red-500 font-semibold text-text mb-1.5">
              {t("survey.fullAddInput")}
            </label>
            <input
              type="text" 
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Enter survey location full address"
              className="w-full rounded-xl border border-orange-300 bg-background px-4 py-3 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
              />
          </div>
        </section>

        {/* ── Section grid ─────────────────────────────────────────────────── */}
        <div>
          <p className="text-sm font-semibold text-text-muted mb-3">
            {t("survey.instruction1")}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {form.sections.map((section, index) => (
              <SectionCard
                key={section.id}
                section={section}
                index={index}
                isComplete={completedSections.has(section.id)}
                onClick={() => setActiveSection(index)}
                surveyType={surveyType}
              />
            ))}
          </div>
        </div>

        {/* ── Final submit — only visible when all done ─────────────────── */}
        {allComplete && (
          <div className="flex justify-end pt-2 border-t border-border">
            <button
              type="button"
              onClick={handleFinalSubmit}
              disabled={loading}
              className="px-8 py-3 rounded-xl bg-accent hover:bg-accent-hover text-white font-semibold text-sm shadow-sm disabled:opacity-50"
            >
              {loading ? "Submitting..." : "Submit survey"}
            </button>
          </div>
        )}
      </div>

      {/* ── Section popup ─────────────────────────────────────────────────── */}
      {activeSection !== null && (
        <SectionPopup
          section={form.sections[activeSection]}
          allSections={form.sections}
          currentIndex={activeSection}
          answers={answers}
          onAnswer={onAnswer}
          onToggleCheckbox={onToggleCheckbox}
          onUpdateField={onUpdateField}
          onBack={() => setActiveSection(null)}
          onSave={handleSaveSection}
          completedSections={completedSections}
          isLastSection={activeSection === form.sections.length - 1}
          isSaving={false}
          surveyType={surveyType}
        />
      )}
    </main>
  );
}

export default SurveyPage;